// Utility functions for time slot and overlap calculations

// Regex for parsing HH:mm:ss or HH:mm
export const TIME_REGEX = /(\d{2}):(\d{2})/;
export const TIME_REGEX_SECONDS = /(\d{2}):(\d{2}):(\d{2})/;

// Find the nearest slot index at or after a given time
export function findNearestSlotIdxAfter(timeStr, slots) {
  const [h, m] = timeStr.split(':');
  const minutes = parseInt(h) * 60 + parseInt(m);
  let minIdx = 0;
  let minDiff = Infinity;
  for (let i = 0; i < slots.length; i++) {
    const [sh, sm] = slots[i].split(':');
    const slotMinutes = parseInt(sh) * 60 + parseInt(sm);
    if (slotMinutes >= minutes && slotMinutes - minutes < minDiff) {
      minDiff = slotMinutes - minutes;
      minIdx = i;
    }
  }
  return minIdx;
}

// Find the nearest slot index at or before a given time
export function findNearestSlotIdxBefore(timeStr, slots) {
  const [h, m] = timeStr.split(':');
  const minutes = parseInt(h) * 60 + parseInt(m);
  let maxIdx = 0;
  let minDiff = Infinity;
  for (let i = 0; i < slots.length; i++) {
    const [sh, sm] = slots[i].split(':');
    const slotMinutes = parseInt(sh) * 60 + parseInt(sm);
    if (slotMinutes <= minutes && minutes - slotMinutes < minDiff) {
      minDiff = minutes - slotMinutes;
      maxIdx = i;
    }
  }
  return maxIdx;
}

// Get the slot index for a given time string ("HH:mm:ss+00" or "HH:mm:ss")
export function getSlotIndex(timeStr, timeSlots) {
  if (!timeStr) return -1;
  const match = timeStr.match(TIME_REGEX);
  if (!match) return -1;
  const hhmm = `${match[1]}:${match[2]}`;
  return timeSlots.indexOf(hhmm);
}

// Normalize time string to HH:mm format
export function normalizeTimeString(timeStr) {
  if (!timeStr) return '';
  const match = timeStr.match(TIME_REGEX);
  return match ? `${match[1]}:${match[2]}` : '';
}

// Parse time string to minutes since midnight
export function parseTimeToMinutes(timeStr) {
  const match = timeStr.match(TIME_REGEX);
  if (!match) return 0;
  return parseInt(match[1]) * 60 + parseInt(match[2]);
}

// Helper to precompute slot indices for an agenda item
export function computeSlotIndices(item, slots) {
  const sMatch = (item.startTime || item.start_time)?.match(TIME_REGEX);
  const eMatch = (item.endTime || item.end_time)?.match(TIME_REGEX);
  const sTime = sMatch ? `${sMatch[1]}:${sMatch[2]}` : '';
  const eTime = eMatch ? `${eMatch[1]}:${eMatch[2]}` : '';
  const sIdx = findNearestSlotIdxAfter(sTime, slots);
  const eIdx = findNearestSlotIdxBefore(eTime, slots);
  return { ...item, _startIdx: sIdx, _endIdx: eIdx };
}

// Greedy column assignment for a list of items with _startIdx/_endIdx
export function assignColumns(items) {
  const sorted = items.slice().sort((a, b) => {
    if (a._startIdx !== b._startIdx) return a._startIdx - b._startIdx;
    return (a.agendaId || a.id || '').localeCompare(b.agendaId || b.id || '');
  });
  const columns = [];
  sorted.forEach(ev => {
    let col = 0;
    while (true) {
      const conflict = columns[col]?.some(other => {
        return !(ev._endIdx < other._startIdx || other._endIdx < ev._startIdx);
      });
      if (!conflict) break;
      col++;
    }
    if (!columns[col]) columns[col] = [];
    columns[col].push(ev);
    ev._column = col;
  });
  return { itemsWithColumns: sorted, maxColumns: columns.length };
}

// Returns the height in px for an event box
export function getEventBoxHeight(item, slots, slotHeight = 48, slotDuration = 30) {
  const startMatch = (item.startTime || item.start_time)?.match(TIME_REGEX_SECONDS) || item.startTime?.match(TIME_REGEX) || item.start_time?.match(TIME_REGEX);
  const endMatch = (item.endTime || item.end_time)?.match(TIME_REGEX_SECONDS) || item.endTime?.match(TIME_REGEX) || item.end_time?.match(TIME_REGEX);
  if (!startMatch || !endMatch) return 18;
  const startTime = `${startMatch[1]}:${startMatch[2]}`;
  const endTime = `${endMatch[1]}:${endMatch[2]}`;
  const startIdx = findNearestSlotIdxAfter(startTime, slots);
  const endIdx = findNearestSlotIdxBefore(endTime, slots);
  const startMinutes = parseInt(startMatch[1]) * 60 + parseInt(startMatch[2]);
  const endMinutes = parseInt(endMatch[1]) * 60 + parseInt(endMatch[2]);
  const slotStartMinutes = slots[startIdx] ? (parseInt(slots[startIdx].split(':')[0]) * 60 + parseInt(slots[startIdx].split(':')[1])) : 0;
  const slotEndMinutes = slots[endIdx] ? (parseInt(slots[endIdx].split(':')[0]) * 60 + parseInt(slots[endIdx].split(':')[1])) : 0;
  const topOffset = Math.max(0, startMinutes - slotStartMinutes);
  const bottomOffset = Math.max(0, (slotEndMinutes + slotDuration) - endMinutes);
  const totalMinutes = endMinutes - startMinutes;
  let heightPx = 0;
  if (startIdx === endIdx) {
    heightPx = (totalMinutes / slotDuration) * slotHeight;
  } else {
    heightPx = (endIdx - startIdx + 1) * slotHeight;
    heightPx -= (topOffset / slotDuration) * slotHeight;
    heightPx -= (bottomOffset / slotDuration) * slotHeight;
  }
  return Math.max(heightPx, 18);
} 